import { iCity } from "core/kangarooTypes/iCity";
import { Plus, Pencil, Trash2, Loader } from "lucide-react";
import { useEffect, useState } from "react";
import Button from "shared/Button";
import CityFormDialog from "./CityFormDialog";
import { getCites } from "core/Services/CommonServices";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { addCity, updateCity, deleteCity } from "core/Services/AdminServices";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

// Skeleton component for loading state
const CityTableSkeleton = () => {
  const { t } = useTranslation();
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead>
          <tr className="bg-gray-50">
            <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">
              {t("cityManagement.cityId")}
            </th>
            <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">
              {t("cityManagement.cityName")}
            </th>
            <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">
              {t("cityManagement.deliveryFee")}
            </th>
            <th className="px-6 py-4 text-right text-sm font-medium text-gray-500">
              {t("cityManagement.actions")}
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {[...Array(5)].map((_, index) => (
            <tr key={index} className="hover:bg-gray-50">
              <td className="px-6 py-4 whitespace-nowrap">
                <Skeleton width={40} />
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <Skeleton width={150} />
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <Skeleton width={80} />
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right">
                <div className="flex justify-end gap-2">
                  <Skeleton width={20} height={20} circle />
                  <Skeleton width={20} height={20} circle />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// City Management Component
const CityManagement = () => {
  const { t } = useTranslation();
  const [cities, setCities] = useState<iCity[]>();
  const [showModal, setShowModal] = useState(false);
  const [editingCity, setEditingCity] = useState<iCity | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [deletingCityId, setDeletingCityId] = useState<number | null>(null);

  const handleAddCity = () => {
    setEditingCity(null);
    setShowModal(true);
  };

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const response = await getCites();
      setCities(response.data);
    } catch (error) {
      toast.error(t("cityManagement.failedToFetch"));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleEditCity = (city: iCity) => {
    setEditingCity(city);
    setShowModal(true);
  };

  const handleDeleteCity = async (id: number) => {
    try {
      setDeletingCityId(id);
      await deleteCity(id);
      fetchData();
      toast.success(t("cityManagement.deleteSuccess"));
    } catch (error) {
      toast.error(t("cityManagement.deleteFailed"));
    } finally {
      setDeletingCityId(null);
    }
  };

  const handleSubmit = async (data: iCity) => {
    try {
      if (editingCity) {
        await updateCity(data, editingCity.id);
        toast.success(t("cityManagement.updateSuccess"));
      } else {
        await addCity(data);
        toast.success(t("cityManagement.addSuccess"));
      }
      fetchData();
      setShowModal(false);
    } catch (error) {
      toast.error(t("cityManagement.saveFailed"));
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <div
        className="flex flex-col gap-2 md:flex-row md:justify-between 
      mb-6 w-full"
      >
        <h2 className="text-xl font-medium text-gray-900">
          {t("cityManagement.title")}
        </h2>
        <Button onClick={handleAddCity} icon={<Plus size={16} />}>
          {t("cityManagement.addNewCity")}
        </Button>
      </div>

      {isLoading ? (
        <CityTableSkeleton />
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">
                  {t("cityManagement.cityId")}
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">
                  {t("cityManagement.cityName")}
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">
                  {t("cityManagement.deliveryFee")}
                </th>
                <th className="px-6 py-4 text-right text-sm font-medium text-gray-500">
                  {t("cityManagement.actions")}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {cities?.map((city) => (
                <tr key={city.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {city.id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {city.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    ${city.price.toFixed(2)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end gap-2">
                      <button
                        className="text-blue-600 hover:text-blue-900"
                        onClick={() => handleEditCity(city)}
                        title={t("cityManagement.edit")}
                      >
                        <Pencil className="h-5 w-5" />
                      </button>
                      <button
                        className={`text-red-600 hover:text-red-900 ${
                          deletingCityId === city.id
                            ? "opacity-50 cursor-not-allowed"
                            : ""
                        }`}
                        onClick={() => handleDeleteCity(city.id)}
                        disabled={deletingCityId === city.id}
                        title={t("cityManagement.delete")}
                      >
                        {deletingCityId === city.id ? (
                          <Loader className="h-5 w-5 animate-spin" />
                        ) : (
                          <Trash2 className="h-5 w-5" />
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* City Form Dialog */}
      <CityFormDialog
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onSubmit={handleSubmit}
        initialData={editingCity || undefined}
        isEditing={!!editingCity}
      />
    </div>
  );
};

export default CityManagement;
